// src/components/organizer/OrganizerAnalyticsWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Grid,
  Paper,
  LinearProgress,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  EmojiEvents as TrophyIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Repeat as RepeatIcon,
  Assessment as ReportIcon,
  Timeline as TimelineIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const OrganizerAnalyticsWidget = ({ data }) => {
  const [tabValue, setTabValue] = useState(0);

  if (!data) return null;

  const analyticsData = [
    {
      metric: 'Tasa de Éxito',
      value: `${data.eventSuccess}%`,
      trend: '+5%',
      trendUp: true,
      description: 'Eventos completados exitosamente',
      icon: <TrophyIcon />,
      color: 'success'
    },
    {
      metric: 'Satisfacción',
      value: `${data.participantSatisfaction}★`,
      trend: '+0.2',
      trendUp: true,
      description: 'Calificación promedio de participantes',
      icon: <StarIcon />,
      color: 'warning'
    },
    {
      metric: 'Retención',
      value: `${data.repeatParticipants}%`,
      trend: '+8%',
      trendUp: true,
      description: 'Participantes que regresan',
      icon: <RepeatIcon />,
      color: 'info'
    },
    {
      metric: 'Promedio Participantes',
      value: data.averageParticipants,
      trend: '+12',
      trendUp: true,
      description: 'Participantes por evento',
      icon: <PeopleIcon />,
      color: 'primary'
    }
  ];

  const eventPerformance = [
    { event: 'Maratón Ciudad', satisfaction: 4.9, participants: 450, revenue: 67500, status: 'success' },
    { event: 'Copa Juvenil', satisfaction: 4.7, participants: 120, revenue: 18000, status: 'success' },
    { event: 'Triatlón Regional', satisfaction: 4.8, participants: 85, revenue: 12750, status: 'ongoing' },
    { event: 'Carrera 5K', satisfaction: 4.5, participants: 200, revenue: 15000, status: 'completed' }
  ];

  const monthlyTrends = [
    { month: 'Oct', events: 3, participants: 285, revenue: 42750, satisfaction: 4.6 },
    { month: 'Nov', events: 4, participants: 380, revenue: 57000, satisfaction: 4.7 },
    { month: 'Dic', events: 5, participants: 585, revenue: 87750, satisfaction: 4.8 }
  ];

  const getStatusColor = (status) => {
    const colors = {
      success: 'success',
      ongoing: 'primary',
      completed: 'info',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      success: 'Exitoso',
      ongoing: 'En Curso',
      completed: 'Completado',
      cancelled: 'Cancelado'
    };
    return labels[status] || status;
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="📊 Análisis de Rendimiento"
        subheader="Métricas de mis eventos"
        action={
          <Box display="flex" gap={1}>
            <Tooltip title="Actualizar datos">
              <IconButton size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Chip 
              label={`+${data.monthlyGrowth}%`} 
              color="success" 
              size="small"
              icon={<TrendingUpIcon />}
            />
          </Box>
        }
      />
      
      <CardContent>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Métricas" />
          <Tab label="Eventos" />
          <Tab label="Tendencias" />
        </Tabs>

        {/* Tab 1: Métricas principales */}
        {tabValue === 0 && (
          <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {analyticsData.map((metric, index) => (
                <Grid item xs={6} key={index}>
                  <Paper sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Avatar sx={{ bgcolor: `${metric.color}.main`, width: 32, height: 32 }}>
                        {React.cloneElement(metric.icon, { fontSize: 'small' })}
                      </Avatar>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {metric.metric}
                      </Typography>
                    </Box>
                    
                    <Typography variant="h5" fontWeight="bold" color={`${metric.color}.main`} gutterBottom>
                      {metric.value}
                    </Typography>
                    
                    <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                      {metric.trendUp ? (
                        <TrendingUpIcon color="success" fontSize="small" />
                      ) : (
                        <TrendingDownIcon color="error" fontSize="small" />
                      )}
                      <Typography 
                        variant="caption" 
                        color={metric.trendUp ? 'success.main' : 'error.main'}
                      >
                        {metric.trend}
                      </Typography>
                    </Box>
                    
                    <Typography variant="caption" color="text.secondary">
                      {metric.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Objetivos del mes */}
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                🎯 Objetivos del Mes
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Meta Eventos
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      5 eventos
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(4 / 5) * 100}
                      color="primary"
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      4 de 5 completados
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Meta Satisfacción
                    </Typography>
                    <Typography variant="h6" color="warning.main">
                      4.5★
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(4.8 / 5) * 100}
                      color="warning"
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      4.8★ actual (Superado!)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}

        {/* Tab 2: Rendimiento por evento */}
        {tabValue === 1 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              🏆 Rendimiento por Evento
            </Typography>
            
            <List dense>
              {eventPerformance.map((event, index) => (
                <ListItem key={index} divider>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: `${getStatusColor(event.status)}.main`, width: 32, height: 32 }}>
                      <TrophyIcon fontSize="small" />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight="bold">
                          {event.event}
                        </Typography>
                        <Chip
                          label={getStatusLabel(event.status)}
                          color={getStatusColor(event.status)}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          ⭐ {event.satisfaction} • 👥 {event.participants} • 💰 ${event.revenue.toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {/* Insights */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="info.dark" gutterBottom>
                💡 Insights
              </Typography>
              <Typography variant="body2" color="info.dark">
                • Tu evento mejor valorado es el Maratón Ciudad (4.9★)
                <br />
                • Los eventos de atletismo generan mayor satisfacción
                <br />
                • Promedio de ingresos por participante: $150
              </Typography>
            </Box>
          </Box>
        )}

        {/* Tab 3: Tendencias mensuales */}
        {tabValue === 2 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              📈 Evolución Mensual
            </Typography>
            
            {monthlyTrends.map((month, index) => (
              <Paper key={index} sx={{ p: 2, mb: 2, border: 1, borderColor: 'divider' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" fontWeight="bold">
                    {month.month} 2024
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {month.events} eventos
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box textAlign="center">
                      <Typography variant="h6" color="success.main">
                        {month.participants}
                      </Typography>
                      <Typography variant="caption">Participantes</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box textAlign="center">
                      <Typography variant="h6" color="info.main">
                        ${month.revenue.toLocaleString()}
                      </Typography>
                      <Typography variant="caption">Ingresos</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box textAlign="center">
                      <Typography variant="h6" color="warning.main">
                        {month.satisfaction}★
                      </Typography>
                      <Typography variant="caption">Satisfacción</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}

            {/* Proyección */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="success.dark" gutterBottom>
                🔮 Proyección Enero 2025
              </Typography>
              <Typography variant="body2" color="success.dark">
                Basado en la tendencia actual: 6 eventos • 750 participantes • $112,500 en ingresos
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default OrganizerAnalyticsWidget;